// Ops · Team (docs/OPS-PEOPLE-CONTRACT.md "Back office"): admins and riders, invite by email,
// remove a role, and the audit trail. The server enforces every guard (last admin, rider on a job);
// these mirror them so the screen can explain a disabled button before anyone clicks it.
// Pure: no DOM, no Intl. node --test runs it.

/** @typedef {import('@popcart/core/v1').TeamMember} TeamMember */
/** @typedef {import('@popcart/core/v1').TeamRole} TeamRole */
/** @typedef {import('@popcart/core/v1').AuditEntry} AuditEntry */

export const teamKeys = {
  all: ['ops', 'team'],
  /** @param {TeamRole} role */
  list: (role) => ['ops', 'team', role],
  audit: ['ops', 'audit'],
};

/** Audit rows per "Load more". */
export const AUDIT_PAGE = 50;

export const ROLE_LABEL = /** @type {const} */ ({ platform_admin: 'Admin', rider: 'Rider' });

/** @type {{ value: TeamRole; label: string; help: string }[]} */
export const ROLE_OPTIONS = [
  { value: 'platform_admin', label: 'Admin', help: 'Opens PopCart Ops: orders, shops, money and settings. Sessions last 12 hours.' },
  { value: 'rider', label: 'Rider', help: 'Signs in to the PopCart Rider app and takes deliveries once their profile is complete.' },
];

export const MAX_EMAIL = 254;
export const MAX_NAME = 80;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LAST_ADMIN = 'PopCart Ops always keeps at least one admin. Add another admin first.';
export const RIDER_ON_JOB = 'This rider is on a delivery right now. Remove them once it’s done.';

/** Trimmed, lower-case. @param {unknown} raw */
export function normalizeEmail(raw) {
  return String(raw ?? '').trim().toLowerCase();
}

/** The name to show: name, else the email, else a placeholder. @param {Pick<TeamMember, 'name' | 'email'>} m */
export function memberName(m) {
  return (m.name && m.name.trim()) || m.email || 'Invited person';
}

/**
 * Check the invite form. Returns the request body, or field errors.
 * @param {{ email: string; name: string; role: TeamRole | '' }} draft
 * @param {TeamMember[]} [existing] people already holding `draft.role`
 * @returns {{ ok: true; value: { email: string; role: TeamRole; name?: string } } | { ok: false; errors: Partial<Record<'email' | 'name' | 'role', string>> }}
 */
export function validateInvite(draft, existing = []) {
  /** @type {Partial<Record<'email' | 'name' | 'role', string>>} */
  const errors = {};
  const email = normalizeEmail(draft.email);
  const name = String(draft.name ?? '').trim();
  const role = draft.role;
  if (!email) errors.email = 'Enter their email.';
  else if (email.length > MAX_EMAIL || !EMAIL_RE.test(email)) errors.email = 'That email doesn’t look right. Check it and try again.';
  else if (role && existing.some((m) => normalizeEmail(m.email) === email && m.roles.includes(role))) {
    errors.email = `That person is already ${role === 'platform_admin' ? 'an admin' : 'a rider'}.`;
  }
  if (name && name.length < 2) errors.name = 'At least 2 letters, or leave it blank.';
  else if (name.length > MAX_NAME) errors.name = `${MAX_NAME} characters at most.`;
  if (role !== 'platform_admin' && role !== 'rider') errors.role = 'Pick a role.';
  if (Object.keys(errors).length) return { ok: false, errors };
  /** @type {{ email: string; role: TeamRole; name?: string }} */
  const value = { email, role: /** @type {TeamRole} */ (role) };
  if (name) value.name = name;
  return { ok: true, value };
}

/**
 * Why this role can't be removed right now (null = it can). Mirrors the API's 409 guards.
 * @param {TeamMember} m
 * @param {TeamRole} role
 * @param {TeamMember[]} list everyone holding `role`
 */
export function removeBlock(m, role, list) {
  if (role === 'platform_admin' && list.filter((x) => x.roles.includes('platform_admin')).length <= 1) return LAST_ADMIN;
  if (role === 'rider' && (m.rider?.activeJobs ?? 0) > 0) return RIDER_ON_JOB;
  return null;
}

/**
 * The sentence for a failed invite or remove. A 409 on remove is one of the two refusals, said
 * plainly whatever the server's wording; anything else uses the server's own message.
 * @param {{ status?: number; code?: string; message?: string } | null | undefined} err
 * @param {'invite' | 'remove'} action
 * @param {TeamRole} [role]
 * @param {(err: any, fallback: string) => string} [voice] the app's errorMessage
 */
export function teamProblem(err, action, role, voice) {
  if (action === 'remove' && err?.status === 409) return role === 'rider' ? RIDER_ON_JOB : LAST_ADMIN;
  if (action === 'invite' && err?.status === 409) return err.message && !/^Request failed/.test(err.message) ? err.message : 'That person already has this role.';
  const fallback = action === 'invite' ? 'Couldn’t send the invite. Try again.' : 'Couldn’t remove them. Try again.';
  return voice ? voice(err, fallback) : fallback;
}

/** "Remove Ana as admin?" copy for the confirm dialog. @param {TeamMember} m @param {TeamRole} role */
export function removeCopy(m, role) {
  const name = memberName(m);
  if (role === 'platform_admin') {
    return {
      title: `Remove ${name} as an admin?`,
      body: 'They lose access to PopCart Ops at their next request. Their account and order history stay.',
      confirm: 'Remove admin',
    };
  }
  return {
    title: `Remove ${name} as a rider?`,
    body: 'They can’t go online or take deliveries any more. Their past deliveries and cash records stay.',
    confirm: 'Remove rider',
  };
}

/** Newest first, then by name. @param {TeamMember[]} list */
export function sortMembers(list) {
  return list.slice().sort((a, b) => {
    if (a.addedAt !== b.addedAt) return a.addedAt < b.addedAt ? 1 : -1;
    return memberName(a).localeCompare(memberName(b));
  });
}

/**
 * Riders: who is ready. Profile complete / online / active jobs, as words.
 * @param {TeamMember} m
 * @returns {{ complete: boolean; online: boolean; activeJobs: number; profileLabel: string; onlineLabel: string; jobsLabel: string }}
 */
export function riderState(m) {
  const r = m.rider ?? { complete: false, online: false, activeJobs: 0 };
  const jobs = Math.max(0, Number(r.activeJobs) || 0);
  return {
    complete: !!r.complete,
    online: !!r.online,
    activeJobs: jobs,
    profileLabel: r.complete ? 'Profile complete' : 'Profile incomplete',
    onlineLabel: r.online ? 'Online' : 'Offline',
    jobsLabel: jobs === 0 ? 'No active jobs' : jobs === 1 ? '1 active job' : `${jobs} active jobs`,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

/** "10 Oct, 14:05" in local time ("—" for nothing). @param {string | null | undefined} iso */
export function stamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Added 10 Oct" / "Last seen 10 Oct, 14:05" / "Not signed in yet". @param {TeamMember} m */
export function memberMeta(m) {
  const added = m.addedAt ? `Added ${stamp(m.addedAt).split(',')[0]}` : '';
  const seen = m.lastSeenAt ? `Last seen ${stamp(m.lastSeenAt)}` : 'Not signed in yet';
  return [added, seen].filter(Boolean).join(' · ');
}

const ACTION_LABEL = /** @type {Record<string, string>} */ ({
  'role.grant': 'Role added',
  'role.revoke': 'Role removed',
  'shop.create': 'Shop added',
  'shop.update': 'Shop edited',
  'shop.status': 'Shop status changed',
  'shop.verify': 'Shop verified',
  'invite.sent': 'Invite sent',
});

/** Short label for an audit action ("Role added"); unknown actions pass through. @param {string} action */
export function actionLabel(action) {
  return ACTION_LABEL[action] ?? action;
}

/** Which pill tone an action reads as. @param {string} action @returns {'success' | 'warning' | 'error' | 'info' | 'neutral'} */
export function actionTone(action) {
  if (action === 'role.revoke') return 'warning';
  if (action === 'shop.verify' || action === 'role.grant') return 'success';
  if (action === 'shop.create' || action === 'invite.sent') return 'info';
  return 'neutral';
}

/**
 * One audit row in words: who, what, when. The server's `summary` wins; an empty one gets a line
 * built from the action and target.
 * @param {AuditEntry} e
 */
export function auditView(e) {
  const who = (e.actor?.name && e.actor.name.trim()) || (e.actor?.userId ? 'An admin' : 'PopCart');
  const summary = (e.summary && e.summary.trim()) || `${actionLabel(e.action)}${e.targetType === 'shop' ? ' (shop)' : ' (person)'}`;
  return { id: e.id, who, summary, label: actionLabel(e.action), tone: actionTone(e.action), when: stamp(e.at) };
}

/**
 * Append a page of audit rows, keeping order and dropping repeats (a row can straddle two pages
 * when new rows land between requests).
 * @param {AuditEntry[]} have
 * @param {AuditEntry[]} page
 */
export function appendAudit(have, page) {
  const seen = new Set(have.map((e) => e.id));
  return [...have, ...page.filter((e) => !seen.has(e.id))];
}

/**
 * The page's calls, in one place (tests drive them against a mock client).
 * @param {Pick<import('@popcart/core/v1').PopCartApi, 'inviteTeamMember' | 'revokeTeamRole' | 'opsAudit'>} api
 */
export function teamActions(api) {
  return {
    /** @param {{ email: string; role: TeamRole; name?: string }} body */
    invite: (body) => api.inviteTeamMember(body),
    /** @param {string} userId @param {TeamRole} role */
    revoke: (userId, role) => api.revokeTeamRole(userId, role),
    /** @param {string | null} [before] */
    audit: (before) => api.opsAudit(before ? { limit: AUDIT_PAGE, before } : { limit: AUDIT_PAGE }),
  };
}
